import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBell } from '@fortawesome/pro-solid-svg-icons';
import { interval, startWith, switchMap } from 'rxjs';
import { AppNotification } from '../../../core/models/notification.models';
import { NotificationsStoreService } from '../../../core/services/notifications-store.service';
import { navigateToNotificationTarget } from '../../../core/utils/notification-navigation';

const POLL_INTERVAL_MS = 60000;

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './notification-bell.component.html',
})
export class NotificationBellComponent {
  private readonly store = inject(NotificationsStoreService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);

  protected readonly faBell = faBell;
  protected readonly isOpen = signal(false);

  protected readonly notifications = this.store.notifications;
  protected readonly unreadCount = this.store.unreadCount;

  constructor() {
    interval(POLL_INTERVAL_MS)
      .pipe(startWith(0), switchMap(() => this.refresh()), takeUntilDestroyed())
      .subscribe();
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (this.isOpen() && !this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }

  protected toggle(): void {
    this.isOpen.update((value) => !value);
  }

  protected async onNotificationClick(notification: AppNotification): Promise<void> {
    await this.store.markRead(notification.id);
    this.isOpen.set(false);

    navigateToNotificationTarget(this.router, notification);
  }

  protected async markAllRead(): Promise<void> {
    await this.store.markAllRead();
  }

  protected goToAll(): void {
    this.isOpen.set(false);
    this.router.navigateByUrl('/notificaciones');
  }

  private async refresh(): Promise<void> {
    try {
      await this.store.refresh();
    } catch {
      // Silently ignore polling failures; next tick will retry.
    }
  }
}
